import {startBookingAttribution} from './lib/bookingAttribution';
import './lib/errorMonitoring';
import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./styles/premium-animations.css";
import "./styles/deferred-animations.css";
import "./lib/i18n";
import { ThemeProvider } from "./hooks/use-theme";
import { LanguageProvider } from "./hooks/use-language";
import { deferCriticalCSS } from "./lib/imageOptimization";
import { recoverFromChunkError } from "./lib/chunkRecovery";

// Vite raises this when a lazy chunk or its CSS fails to load (typically a tab left open across a deploy).
window.addEventListener('vite:preloadError', (event) => {
  if (recoverFromChunkError()) event.preventDefault();
});

type AppComponent = React.ComponentType;

const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';

// Capture the consented landing before downloading the lazy route graph. A slow
// or interrupted booking chunk must not discard the visitor's original click.
startBookingAttribution();

// Keep the homepage entry independent from the booking/staff/admin application.
// The production HTML already shows its prerendered hero while this small route
// entry downloads, so React is enhancement rather than a first-paint blocker.
const appPromise: Promise<{ default: AppComponent }> = normalizedPath === '/'
  ? import('./homepage-app')
  : import('./App');
// Mobile detection (simplified since custom cursor now handles its own hiding)
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) ||
    window.innerWidth <= 768;
};

if (isMobile()) {
  document.body.classList.add('mobile-device');
  document.documentElement.setAttribute('data-mobile', 'true');
}

const BUILD_ID = __ZAVIRA_BUILD_ID__;

async function clearOldCachesOnNewDeploy() {
  if (import.meta.env.DEV) return;

  try {
    const key = 'zavira_build_id';
    const last = localStorage.getItem(key);

    if (last === BUILD_ID) return;
    localStorage.setItem(key, BUILD_ID);

    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.allSettled(
        regs.map(async (reg) => {
          try {
            reg.active?.postMessage({ type: 'CLEAR_CACHE' });
          } catch {
            // A stale worker may disappear between enumeration and messaging.
          }
          try {
            // Updating preserves the registration and its PushSubscription.
            // Unregistering on every deploy invalidates installed-app push endpoints.
            await reg.update();
          } catch {
            // Keep the existing worker/subscription when an update is offline.
          }
        })
      );
    }

    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.allSettled(keys.map((k) => caches.delete(k)));
    }

    // The homepage already loaded this build from network-first HTML. A reload
    // here restarts its intro/video after they have appeared. Update caches
    // in the background and let the next navigation use the new worker.
    const activePath = window.location.pathname.replace(/\/+$/, '') || '/';
    if (last && activePath !== '/' && !/^\/booking(?:\/|$)/.test(activePath)) {
      window.location.reload();
    }
  } catch {
    // Ignore storage/cache errors.
  }
}

function ensureServiceWorkerRegistered() {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // First installation and later updates must not replay the homepage intro.
    const activePath = window.location.pathname.replace(/\/+$/, '') || '/';
    // Booking drafts include live review state. A worker update must not
    // interrupt a customer or race an in-flight appointment submission.
    if (activePath === '/' || /^\/booking(?:\/|$)/.test(activePath)) return;
    const reloadKey = `zavira_sw_reload_${BUILD_ID}`;
    if (sessionStorage.getItem(reloadKey)) return;

    sessionStorage.setItem(reloadKey, '1');
    window.location.reload();
  });

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      });
      await registration.update();

      // Staff tablets/phones often keep the calendar tab open for days. Check
      // again whenever the app returns to the foreground and periodically so
      // a new worker can activate without waiting for a manual hard refresh.
      const checkForUpdate = () => {
        void registration.update().catch(() => {});
      };
      window.addEventListener('focus', checkForUpdate);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
      window.setInterval(checkForUpdate, 10 * 60 * 1000);
    } catch {
      // Offline/private-mode failures should not block the application shell.
    }
  });
}

void clearOldCachesOnNewDeploy().finally(() => ensureServiceWorkerRegistered());
const RootApp = ({ App }: { App: AppComponent }) => {
  // Defer non-critical CSS after page is interactive (improves FCP/Speed Index)
  React.useEffect(() => {
    window.dispatchEvent(new Event('zavira:app-ready'));
    deferCriticalCSS();
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="zavira-theme">
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </ThemeProvider>
  );
};

void appPromise.then(({ default: App }) => {
createRoot(document.getElementById("root")!).render(<RootApp App={App} />);
});
